import type { Node } from "@react-types/shared";
import { clsx } from "clsx";
import type { MaterialSymbol } from "material-symbols/index.d.ts";
import { type CSSProperties, type ForwardedRef, type ReactElement, type ReactNode, useCallback, useRef } from "react";
import { type AriaSelectProps, mergeProps, useButton, useFocusRing, useSelect, VisuallyHidden } from "react-aria";
import { useSelectState } from "react-stately";

import { useDOMRef, useResizeObserver } from "../../hooks.ts";
import { useDataAttributes } from "../../hooks/useDataAttributes.ts";
import formStyle from "../../styles/form.module.css";
import { forwardRef } from "../../utils/jsx/forwardRef.ts";
import { Badge } from "../Badge/Badge.tsx";
import styleMenu from "../ContextualMenu/ContextualMenu.module.css";
import { Description } from "../Description/Description.tsx";
import { Icon } from "../Icon/Icon.tsx";
import { SymbolIcon } from "../Icon/SymbolIcon.tsx";
import { Label } from "../Label/Label.tsx";
import { ListBox } from "../List/ListBox.tsx";
import { Popover } from "../Popover/Popover.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import { VStack } from "../Stack/VStack.tsx";
import style from "./Select.module.css";

export interface SelectProps<T> extends AriaSelectProps<T> {
  style?: CSSProperties;
  icon?: ReactElement | MaterialSymbol;
  className?: string;
  width?: CSSProperties["minWidth"];
  stackStyle?: CSSProperties;
  rendered?: (selected: Node<T>) => ReactNode;
}

const SelectRender = <T extends object>(props: SelectProps<T>, ref: ForwardedRef<HTMLButtonElement>): ReactNode => {
  const triggerRef = useDOMRef(ref);
  const state = useSelectState<T>(props);
  const {
    autoFocus,
    placeholder,
    isRequired,
    label,
    stackStyle,
    rendered,
    name,
    width,
    className,
    description,
    isInvalid,
    isDisabled,
    icon,
    errorMessage,
  } = props;

  const { labelProps, triggerProps, valueProps, menuProps, descriptionProps, errorMessageProps } = useSelect<T>(
    {
      ...props,
      label: label ?? placeholder,
    },
    state,
    triggerRef,
  );
  const { isFocusVisible, isFocused, focusProps } = useFocusRing({ autoFocus: autoFocus });
  const { buttonProps } = useButton(triggerProps, triggerRef);

  const stackRef = useRef<HTMLDivElement>(null);
  const rect = useResizeObserver(stackRef);
  const doRendered = useCallback(
    (selected: Node<T> | null) => {
      if (selected !== null) {
        if (rendered) {
          return rendered(selected);
        }
        return selected.textValue || selected.rendered;
      }
      return <span className={style.placeholder}>{placeholder ?? "Select an option"}</span>;
    },
    [placeholder, rendered],
  );

  const dataAttributes = useDataAttributes(props);
  const testId = name ?? dataAttributes["data-testid"];

  return (
    <>
      <VStack
        spacing={2}
        style={{ minWidth: width, ...stackStyle }}
        ref={stackRef}
        data-testid={`${testId ? `${testId}__` : ""}select`}
      >
        {label ? (
          <HStack className={formStyle.input_layout_column1} style={{ alignItems: "baseline" }}>
            <Label {...labelProps}>{label}</Label>
            <Spacer />
            {!isRequired && <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />}
          </HStack>
        ) : (
          <VisuallyHidden>
            <label {...labelProps}>{placeholder}</label>
          </VisuallyHidden>
        )}
        <Description
          {...descriptionProps}
          data-testid="select-description"
          className={clsx(formStyle.input_layout_column1, {
            [formStyle.description_with_tag]: label != undefined && !isRequired,
          })}
        >
          {description}
        </Description>
        <button
          type="button"
          {...mergeProps(buttonProps, focusProps)}
          className={clsx(style.select, className, {
            [style.disabled]: isDisabled,
            [style.focus]: isFocusVisible || isFocused,
            [style.error]: isInvalid,
          })}
          style={{ ...props.style }}
          {...dataAttributes}
          data-testid={`${testId ? `${testId}__` : ""}select-button`}
          ref={triggerRef}
        >
          {icon != undefined && <Icon icon={icon} className={style.icon} />}
          <div {...valueProps} className={clsx(style.input, "select-option-item-rendered")}>
            {doRendered(state.selectedItem)}
          </div>
          <div className={style.action} aria-hidden="true">
            {/* Width is 25px because button has `minWidth: 30` and icon is 20px */}
            <SymbolIcon icon={"unfold_more"} style={{ width: 25 }} />
          </div>
        </button>
        {errorMessage != undefined && !!isInvalid && (
          <div role="alert" {...errorMessageProps} className={formStyle.error_message} data-testid="number-field-error">
            {errorMessage as ReactNode}
          </div>
        )}
      </VStack>
      <Popover state={state} triggerRef={triggerRef} placement="bottom" offset={5} containerPadding={10}>
        <ListBox
          {...menuProps}
          state={state}
          className={styleMenu.menu}
          data-testid={"combobox-menu-popover"}
          style={{ minWidth: rect?.width ?? width }}
        />
      </Popover>
    </>
  );
};

export const Select = forwardRef(SelectRender);
